/**
 * CalibrationTakeUploader — upload a single calibration take video.
 *
 * Three take types:
 *   - "Correct" (label="good"): shows the movement done right.
 *   - "Flawed" (label="bad"): shows a specific mistake — should lose points but
 *       NOT score zero.
 *   - "Disqualifying" (label="fail"): a performance that should score zero.
 *
 * On submit:
 *   1. Upload via POST /calibration-takes/upload.
 *   2. Immediately trigger POST /{take_id}/process.
 *   3. Call onTakeQueued(take_id) so the parent can start polling status.
 *
 * Mirrors the compact upload portion of TeachingVideoUploader.tsx.
 */

import { useState, useRef, type ChangeEvent } from 'react';
import { Button, Alert, Spinner } from '@/components/ui';
import {
  useUploadCalibrationTake,
  useProcessCalibrationTake,
} from '@/hooks/useCalibrationTakes';
import { CALIBRATION_FAILURE_DIMENSION_OPTIONS } from '@/utils/constants';
import {
  GRADE_NUMERIC_MAX,
  GRADE_NUMERIC_MIN,
  gradeNumericToDisplayGrade,
  isValidGradeNumeric,
  parseGradeNumericInput,
} from '@/utils/gradeNumeric';
import type { CalibrationTakeLabel } from '@/types/calibrationTake';

const VIDEO_ACCEPT = '.mp4,.mov,.avi,.mkv,.webm,.mpeg';

// Matches backend MAX_GOOD_TAKES / MAX_BAD_TAKES / MAX_FAIL_TAKES
export const MAX_GOOD_TAKES = 5;
export const MAX_BAD_TAKES = 2;
export const MAX_FAIL_TAKES = 3;

// Matches backend reason_text max length (calibration-take upload contract)
export const MAX_REASON_TEXT_LENGTH = 2000;
// Show the live counter once the coach is this close to the cap
const REASON_COUNTER_THRESHOLD = MAX_REASON_TEXT_LENGTH - 200;

// Sourced from utils/constants.ts (single source of truth shared with
// CalibrationTakeEditForm.tsx's multi-select — see that constant's
// docstring for why it lives there and not in either component file).
const FAILURE_DIMENSION_OPTIONS = CALIBRATION_FAILURE_DIMENSION_OPTIONS;

/**
 * Per-label helper — the "short" tier of the two-tier calibration
 * example-video guidance (calibration-upload-guidance). One tight line per
 * label telling the coach which example of that type is most valuable. The
 * "detailed" tier (full explanation of why) lives behind the guide drawer —
 * see data/templateGuideContent.ts's 'calibration-good' / 'calibration-bad' /
 * 'calibration-fail' entries. Deliberately a shorter paraphrase, not a
 * verbatim copy, of that content.
 */
const LABEL_HELPER_TEXT: Record<CalibrationTakeLabel, string> = {
  good: "A performance you'd accept as-is. Your most useful Good is the weakest one you'd still pass.",
  bad: "You'd correct this but still score it. The mildest fault you'd mention is most useful.",
  fail: "This deserves a zero. The mildest performance you'd still fail is most useful.",
};

interface CalibrationTakeUploaderProps {
  templateId: string;
  nGood: number;
  nBad: number;
  nFail: number;
  /** Called when a take has been uploaded + processing triggered. */
  onTakeQueued: (takeId: string) => void;
}

export default function CalibrationTakeUploader({
  templateId,
  nGood,
  nBad,
  nFail,
  onTakeQueued,
}: CalibrationTakeUploaderProps) {
  const [label, setLabel] = useState<CalibrationTakeLabel>('good');
  const [failureDimension, setFailureDimension] = useState<string>('');
  const [gradeNumericInput, setGradeNumericInput] = useState<string>('');
  const [reasonText, setReasonText] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadCalibrationTake(templateId);
  const processMutation = useProcessCalibrationTake(templateId);

  const goodAtLimit = nGood >= MAX_GOOD_TAKES;
  const badAtLimit = nBad >= MAX_BAD_TAKES;
  const failAtLimit = nFail >= MAX_FAIL_TAKES;
  const canUpload =
    label === 'good' ? !goodAtLimit : label === 'bad' ? !badAtLimit : !failAtLimit;

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
    setError(null);
  };

  const handleLabelChange = (newLabel: CalibrationTakeLabel) => {
    setLabel(newLabel);
    setError(null);
    if (newLabel === 'good') {
      setFailureDimension('');
    }
  };

  const handleReasonTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    // Enforce the cap in state (not just via the maxLength attribute) so
    // programmatic value changes can't exceed the backend's limit either.
    setReasonText(e.target.value.slice(0, MAX_REASON_TEXT_LENGTH));
  };

  const handleGradeNumericChange = (e: ChangeEvent<HTMLInputElement>) => {
    setGradeNumericInput(e.target.value);
  };

  // Live letter-grade chip (numeric take-grade entry) — display only, never
  // submitted. null while the input is empty or the typed value is out of
  // range/non-numeric (the range 422 is still enforced server-side; this is
  // just "don't show a nonsense chip").
  const parsedGrade = parseGradeNumericInput(gradeNumericInput);
  const gradeChipLetter =
    parsedGrade !== null && isValidGradeNumeric(parsedGrade)
      ? gradeNumericToDisplayGrade(parsedGrade)
      : null;

  const handleSubmit = async () => {
    if (!file) {
      setError('Please select a video file.');
      return;
    }
    if (!canUpload) {
      const limitMsg: Record<CalibrationTakeLabel, string> = {
        good: `Maximum of ${MAX_GOOD_TAKES} correct takes reached. Remove one first.`,
        bad: `Maximum of ${MAX_BAD_TAKES} flawed takes reached. Remove one first.`,
        fail: `Maximum of ${MAX_FAIL_TAKES} disqualifying takes reached. Remove one first.`,
      };
      setError(limitMsg[label]);
      return;
    }
    if (parsedGrade !== null && !isValidGradeNumeric(parsedGrade)) {
      setError(`Grade must be a number between ${GRADE_NUMERIC_MIN} and ${GRADE_NUMERIC_MAX}.`);
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const dim =
        (label === 'bad' || label === 'fail') && failureDimension
          ? (failureDimension as 'motion' | 'activity' | 'angle')
          : null;

      const trimmedReason = reasonText.trim();

      const uploadResult = await uploadMutation.mutateAsync({
        file,
        label,
        failureDimension: dim,
        reasonText: trimmedReason.length > 0 ? trimmedReason : null,
        gradeNumeric: parsedGrade,
      });

      // Trigger processing immediately (same UX pattern as teaching videos)
      await processMutation.mutateAsync(uploadResult.take_id);

      onTakeQueued(uploadResult.take_id);

      // Reset form
      setFile(null);
      setFailureDimension('');
      setGradeNumericInput('');
      setReasonText('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Take type selector */}
      <div>
        <p className="text-sm font-medium text-text-secondary mb-2">Take type</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleLabelChange('good')}
            className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition-colors ${
              label === 'good'
                ? 'bg-success/10 border-success/50 text-success'
                : 'bg-dark-elevated-2 border-border-subtle text-text-secondary hover:text-text-primary'
            }`}
          >
            Correct
          </button>
          <button
            type="button"
            onClick={() => handleLabelChange('bad')}
            className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition-colors ${
              label === 'bad'
                ? 'bg-warning/10 border-warning/50 text-warning'
                : 'bg-dark-elevated-2 border-border-subtle text-text-secondary hover:text-text-primary'
            }`}
          >
            Flawed
          </button>
          <button
            type="button"
            data-testid="take-label-fail"
            onClick={() => handleLabelChange('fail')}
            className={`flex-1 px-3 py-2 rounded border text-sm font-medium transition-colors ${
              label === 'fail'
                ? 'bg-danger/10 border-danger/50 text-danger'
                : 'bg-dark-elevated-2 border-border-subtle text-text-secondary hover:text-text-primary'
            }`}
          >
            Disqualifying
          </button>
        </div>

        {/* Per-label description + tight "which example is most valuable" helper —
            swaps with `label`. Keyed on `label` so the swap is a real DOM
            mount/unmount (not an in-place attribute update): useAnchorSpy's
            MutationObserver watches for childList mutations to pick up new
            data-guide-section elements (see hooks/useAnchorSpy.ts), and an
            in-place attribute change on a stable node wouldn't trigger that. */}
        <div key={label} data-guide-section={`calibration-${label}`}>
          <p className="text-xs text-text-muted mt-1.5">
            {label === 'good' ? (
              <>Correct takes define how the movement should look. ({nGood}/{MAX_GOOD_TAKES} uploaded)</>
            ) : label === 'bad' ? (
              <>Flawed takes verify the calibration catches specific mistakes — should lose points but NOT score zero. ({nBad}/{MAX_BAD_TAKES} uploaded)</>
            ) : (
              <>A performance that should score zero (e.g. facing the wrong way). ({nFail}/{MAX_FAIL_TAKES} uploaded)</>
            )}
          </p>

          <p
            data-testid="calibration-label-helper"
            className="text-xs text-text-secondary mt-1.5 px-3 py-2 rounded-lg border border-border-subtle bg-dark-elevated-1"
          >
            {LABEL_HELPER_TEXT[label]}
          </p>
        </div>
      </div>

      {/* Grade (score-band) numeric entry — numeric take-grade entry (was a
          letter A-F dropdown; owner asked for real 0-100 numbers since a
          coach's actual grades read like "85" or "90-94", which letters
          can't express). A SEPARATE control from the take-type selector
          above; offered on EVERY take type. Optional — coaches are free to
          skip it. */}
      <div>
        <label htmlFor="grade-numeric" className="text-sm font-medium text-text-secondary">
          Roughly what score band does this attempt deserve?{' '}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <div className="mt-1 flex items-center gap-2">
          <input
            id="grade-numeric"
            data-testid="grade-numeric-input"
            type="number"
            inputMode="numeric"
            min={GRADE_NUMERIC_MIN}
            max={GRADE_NUMERIC_MAX}
            step={1}
            value={gradeNumericInput}
            onChange={handleGradeNumericChange}
            placeholder="e.g. 85"
            className="w-28 px-3 py-2 border border-border-subtle rounded bg-dark-elevated-2
              text-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          />
          {gradeChipLetter && (
            <span
              data-testid="grade-band-chip"
              className="text-xs font-medium px-2 py-1 rounded-full bg-dark-elevated-1 border border-border-subtle text-text-secondary"
            >
              Reads as {gradeChipLetter}
            </span>
          )}
        </div>
        <p className="text-xs text-text-muted mt-1.5">
          Think of it as a school grade — 85 reads as a solid B. This
          teaches the scoring scale — it does not change how this take
          itself is scored.
        </p>
      </div>

      {/* Failure dimension selector — for bad and fail takes */}
      {(label === 'bad' || label === 'fail') && (
        <div>
          <label
            htmlFor="failure-dimension"
            className="text-sm font-medium text-text-secondary"
          >
            What kind of mistake does this take show?
          </label>
          <select
            id="failure-dimension"
            value={failureDimension}
            onChange={(e) => setFailureDimension(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border-subtle rounded bg-dark-elevated-2
              text-text-primary text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          >
            {FAILURE_DIMENSION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Reason text — coach's explanation of why this take is labeled this way.
          Optional; becomes coaching context (day-one consumer: narrative +
          audit + provenance matcher — see coach-knowledge-codification-epic).
          data-guide-section drives the calibration-upload-guidance drawer's
          "Why this example" content (data/templateGuideContent.ts). */}
      <div data-guide-section="calibration-reason">
        <label htmlFor="reason-text" className="text-sm font-medium text-text-secondary">
          Why is this take labeled this way?{' '}
          <span className="text-text-muted font-normal">(optional)</span>
        </label>
        <textarea
          id="reason-text"
          value={reasonText}
          onChange={handleReasonTextChange}
          maxLength={MAX_REASON_TEXT_LENGTH}
          rows={3}
          placeholder={
            label === 'good'
              ? 'What makes this take a good reference — e.g. "full depth, knee tracks over toe"'
              : 'Describe what\'s wrong — e.g. "knee collapses inward on the landing"'
          }
          className="mt-1 w-full resize-none px-3 py-2 border border-border-subtle rounded bg-dark-elevated-2
            text-text-primary text-sm placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="flex items-center justify-between mt-1">
          <p className="text-xs text-text-muted">
            {label === 'good'
              ? 'Becomes coaching context attached to this take.'
              : 'Becomes coaching context — the more specific, the better the guidance athletes get.'}
          </p>
          {reasonText.length >= REASON_COUNTER_THRESHOLD && (
            <p
              className={`text-xs flex-shrink-0 ml-2 ${
                reasonText.length >= MAX_REASON_TEXT_LENGTH ? 'text-danger' : 'text-text-muted'
              }`}
            >
              {reasonText.length}/{MAX_REASON_TEXT_LENGTH}
            </p>
          )}
        </div>
      </div>

      {/* File picker */}
      <div>
        <label className="text-sm font-medium text-text-secondary">Video file</label>
        <input
          ref={fileInputRef}
          type="file"
          accept={VIDEO_ACCEPT}
          onChange={handleFileChange}
          className="mt-1 block w-full text-sm text-text-secondary
            file:mr-3 file:py-2 file:px-4
            file:rounded file:border-0
            file:text-sm file:font-medium
            file:bg-dark-elevated-1 file:text-text-primary
            file:cursor-pointer file:hover:bg-dark-elevated-2
            cursor-pointer"
        />
        {file && (
          <p className="text-xs text-text-muted mt-1">
            {file.name} ({(file.size / (1024 * 1024)).toFixed(1)} MB)
          </p>
        )}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      {!canUpload && (
        <Alert variant="warning">
          {label === 'good'
            ? `You have reached the maximum of ${MAX_GOOD_TAKES} correct takes.`
            : label === 'bad'
              ? `You have reached the maximum of ${MAX_BAD_TAKES} flawed takes.`
              : `You have reached the maximum of ${MAX_FAIL_TAKES} disqualifying takes.`}{' '}
          Remove an existing take to upload another.
        </Alert>
      )}

      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={isSubmitting || !canUpload}
        className="w-full"
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <Spinner size="sm" />
            Uploading...
          </span>
        ) : (
          'Upload Take'
        )}
      </Button>
    </div>
  );
}
